import React, { useCallback, useState } from "react";

import { Intro } from "components/commons";
import DataTableV2 from "components/commons/data-table/data-table-v2";
import { ConfirmModal, SuccessModal } from "components/modals";
import useDataTable from "hooks/useDataTable";
import { useModal, useRouter, useApi, usePermission } from "hooks";
import locale from "localization";
import { Path, Permission } from "enums";
import { getMovements, deleteMovement } from "apis";

import { mapFilterToRequest, mapDataToList } from "./price-movement.mapper";
import { columns } from "./price-movement-column";
import { initialFilterState } from "./price-movement-filter.state";
import PriceMovementFilter from "./price-movement-filter";

const PriceMovementModule = () => {
  const { history } = useRouter();
  const { can } = usePermission();

  const canCreatePm = can(Permission.PriceMovementCreate);
  const canEditPm = can(Permission.PriceMovementEdit);
  const canDeletePm = can(Permission.PriceMovementDelete);

  const [isDeleting, setIsDeleting] = useState(false);

  const confirmModal = useModal();
  const successModal = useModal();

  const { request: requestDelete } = useApi({
    api: deleteMovement,
    handleOwnError: true,
  });

  const handleDeleteConfirm = useCallback(
    (movement) => {
      confirmModal.show({ movementId: movement.movementId });
    },
    [confirmModal]
  );

  const handleEdit = useCallback(
    (movement) => {
      const id = movement.movementId;
      history.push(Path.EditPriceMovement, { movementId: id });
    },
    [history]
  );

  const { filter, search, table } = useDataTable({
    api: {
      api: getMovements,
      mapper: (param) => param,
    },
    filter: { initialState: initialFilterState(), mapper: mapFilterToRequest },
    table: {
      key: "movements",
      mapper: (movement) =>
        mapDataToList({
          movement,
          canEdit: canEditPm,
          canDelete: canDeletePm,
          onDelete: handleDeleteConfirm,
          onEdit: handleEdit,
        }),
      columns,
    },
  });

  const handleYesDelete = useCallback(async () => {
    const { movementId } = confirmModal;
    setIsDeleting(true);
    try {
      await requestDelete({ movementId });
      confirmModal.close();
      successModal.show();
      table.refetch();
    } catch {
      // error handled by useApi error modal
    } finally {
      setIsDeleting(false);
    }
  }, [confirmModal, successModal, table, requestDelete]);

  return (
    <>
      <ConfirmModal
        {...confirmModal}
        title={locale.deletePriceMovement}
        content={locale.deletePriceMovementContent}
        primary={{
          text: locale.yesDelete,
          onClick: handleYesDelete,
          loading: isDeleting,
        }}
        secondary={{
          text: locale.goBack,
        }}
      />
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.priceMovementSuccessfullyDeleted}
      />
      <Intro
        title={locale.priceMovement}
        {...(canCreatePm && {
          actionText: locale.addPriceMovement,
          actionOnClick: () => history.push(Path.AddPriceMovement),
        })}
      />
      <PriceMovementFilter {...filter} {...search} />
      <DataTableV2 {...table} paginationV2 horizontallyScrollable />
    </>
  );
};

export default PriceMovementModule;
